import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useOrganization, useClerk, useAuth } from '@clerk/clerk-react';
import { useOrgMemberships } from '@/contexts/org-memberships-context';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useResetQueries } from '@/lib/api/trpc';
import { useStaffOrgAutoJoinBySlug } from '@/lib/api/account-manager';
import { PUBLIC_ROUTES } from '@/config/router';
import { Button } from '@/components/ui/button';
import { setActiveWithTimeout } from '@/lib/clerk-set-active';
import { isOrgSwitchNotFoundError } from '@/lib/org-switch-errors';
import { isOrgSlugInMemberships } from '@/lib/org-membership-utils';
import { shouldReloadAfterStaffJoin } from '@/lib/staff-org-join-result';
import {
  clearStaffOrgAutoJoinAttempt,
  hasStaffOrgAutoJoinAttempt,
  markStaffOrgAutoJoinAttempt,
} from '@/lib/staff-org-auto-join-storage';
import { getCrmHomePath } from '@/lib/crm-home-path';
import { isTrpcForbidden } from '@/lib/trpc-error-utils';
import toast from 'react-hot-toast';

const ZERO_MEMBERSHIPS_GRACE_MS = 1_500;
const MAX_SWITCH_ATTEMPTS = 3;

type SwitchState =
  | 'idle'
  | 'staff_joining'
  | 'switching'
  | 'done'
  | 'error'
  | 'denied'
  | 'forbidden';

const Spinner = ({ message }: { message?: string }) => (
  <div className="flex h-screen items-center justify-center">
    <div className="text-center">
      <Loader2 className="text-muted-foreground mx-auto h-8 w-8 animate-spin" />
      {message && <p className="text-muted-foreground mt-4 text-sm">{message}</p>}
    </div>
  </div>
);

type SwitchErrorFallbackProps = {
  orgSlug: string;
  reason: string;
  firstSlug: string | undefined;
  onRetry: () => void;
};

/**
 * Leaving a failed switch on screen is the point: silently redirecting to the
 * operator's first membership dropped staff into an unrelated merchant's
 * dashboard with no error state (#TAG-2096). Going elsewhere stays a click.
 */
function SwitchErrorFallback({ orgSlug, reason, firstSlug, onRetry }: SwitchErrorFallbackProps) {
  const navigate = useNavigate();

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center space-y-4 max-w-md">
        <AlertTriangle className="text-destructive mx-auto h-10 w-10" />
        <h2 className="text-lg font-semibold">Failed to switch organization</h2>
        <p className="text-muted-foreground text-sm">
          Could not open <span className="text-foreground font-medium">{orgSlug}</span>. {reason}
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" onClick={onRetry}>Try again</Button>
          <Button onClick={() => navigate(getCrmHomePath(firstSlug), { replace: true })}>
            Go to default account
          </Button>
        </div>
      </div>
    </div>
  );
}

const reloadCurrentUrl = () => {
  window.location.assign(window.location.href);
};

type WrapperContentProps = {
  orgSlug: string;
  firstSlug: string | undefined;
  memberships: { organization: { slug: string | null } }[] | undefined;
  membershipsLoaded: boolean;
};

const WrapperContent = ({
  orgSlug,
  firstSlug,
  memberships,
  membershipsLoaded,
}: WrapperContentProps) => {
  const clerk = useClerk();
  const { organization } = useOrganization();
  const { clearForOrgSwitch } = useResetQueries();
  const staffJoinMutation = useStaffOrgAutoJoinBySlug();

  const [switchState, setSwitchState] = useState<SwitchState>('idle');
  const [switchError, setSwitchError] = useState<string | null>(null);
  // Retry has to move a dependency of the flow effect, or nothing re-runs and
  // the page sits on a spinner with no way out.
  const [retryNonce, setRetryNonce] = useState(0);
  const switchingRef = useRef(false);
  const attemptCountRef = useRef(0);
  const lastSwitchedSlugRef = useRef<string | null>(null);
  const membershipsRef = useRef(memberships);

  membershipsRef.current = memberships;

  const isAlreadyActive = organization?.slug === orgSlug;

  const handleRetry = useCallback(() => {
    attemptCountRef.current = 0;
    lastSwitchedSlugRef.current = null;
    switchingRef.current = false;
    clearStaffOrgAutoJoinAttempt(orgSlug);
    setSwitchError(null);
    setSwitchState('idle');
    setRetryNonce((n) => n + 1);
  }, [orgSlug]);

  useEffect(() => {
    const previousSlug = lastSwitchedSlugRef.current;
    if (previousSlug !== null && previousSlug !== orgSlug) {
      clearForOrgSwitch();
    }
    if (previousSlug !== orgSlug) {
      attemptCountRef.current = 0;
      switchingRef.current = false;
      setSwitchError(null);
      setSwitchState('idle');
    }
  }, [orgSlug, clearForOrgSwitch]);

  useEffect(() => {
    if (isAlreadyActive) {
      clearStaffOrgAutoJoinAttempt(orgSlug);
      lastSwitchedSlugRef.current = orgSlug;
      if (switchState !== 'done') {
        setSwitchState('done');
      }
      return;
    }

    if (!membershipsLoaded) return;
    if (switchingRef.current) return;

    let cancelled = false;

    const runSetActive = async () => {
      setSwitchState('switching');
      await setActiveWithTimeout((params) => clerk.setActive(params), orgSlug);
      if (cancelled) return;
      clearStaffOrgAutoJoinAttempt(orgSlug);
      lastSwitchedSlugRef.current = orgSlug;
      setSwitchState('done');
    };

    const runStaffJoin = async (): Promise<boolean> => {
      setSwitchState('staff_joining');
      const joinResult = await staffJoinMutation.mutateAsync({ orgSlug });

      if (shouldReloadAfterStaffJoin(joinResult)) {
        markStaffOrgAutoJoinAttempt(orgSlug);
        reloadCurrentUrl();
        return true;
      }

      if (!joinResult.found) {
        const reason = 'Organization not found or you don\'t have access';
        toast.error(reason);
        setSwitchError(reason);
        setSwitchState('denied');
        return true;
      }

      const reason = joinResult.message || 'Unable to access this organization';
      toast.error(reason);
      setSwitchError(reason);
      setSwitchState('denied');
      return true;
    };

    const runFlow = async () => {
      if (switchingRef.current) return;
      switchingRef.current = true;
      attemptCountRef.current += 1;

      try {
        const isMember = isOrgSlugInMemberships(membershipsRef.current, orgSlug);
        const shouldTryStaffJoin =
          !isMember && !hasStaffOrgAutoJoinAttempt(orgSlug);

        if (shouldTryStaffJoin) {
          try {
            const handled = await runStaffJoin();
            if (handled) return;
          } catch (error) {
            if (isTrpcForbidden(error)) {
              const reason = 'You do not have permission to access this client account.';
              clearStaffOrgAutoJoinAttempt(orgSlug);
              toast.error(reason);
              setSwitchError(reason);
              setSwitchState('forbidden');
              return;
            }
            const reason =
              error instanceof Error ? error.message : 'Unable to access this organization';
            toast.error(reason);
            setSwitchError(reason);
            setSwitchState('denied');
            return;
          }
        }

        await runSetActive();
      } catch (err: unknown) {
        if (cancelled) return;
        console.error('[OrgRouteWrapper] setActive failed:', err);

        const isMember = isOrgSlugInMemberships(membershipsRef.current, orgSlug);
        if (
          isOrgSwitchNotFoundError(err)
          && !isMember
          && !hasStaffOrgAutoJoinAttempt(orgSlug)
        ) {
          switchingRef.current = false;
          attemptCountRef.current = 0;
          setSwitchState('idle');
          return;
        }

        if (attemptCountRef.current < MAX_SWITCH_ATTEMPTS) {
          switchingRef.current = false;
          const delay = Math.min(1000 * 2 ** (attemptCountRef.current - 1), 4000);
          setTimeout(() => { if (!cancelled) void runFlow(); }, delay);
          return;
        }

        setSwitchError('Something went wrong while activating this organization. This may be a temporary issue.');
        setSwitchState('error');
      } finally {
        if (!cancelled) switchingRef.current = false;
      }
    };

    void runFlow();

    return () => { cancelled = true; };
  }, [orgSlug, isAlreadyActive, membershipsLoaded, retryNonce]);

  if (switchState === 'forbidden' || switchState === 'denied' || switchState === 'error') {
    return (
      <SwitchErrorFallback
        orgSlug={orgSlug}
        reason={switchError ?? 'This organization could not be opened.'}
        firstSlug={firstSlug}
        onRetry={handleRetry}
      />
    );
  }

  if (!isAlreadyActive) {
    const message = switchState === 'staff_joining'
      ? 'Connecting to client account...'
      : 'Switching organization...';
    return <Spinner message={message} />;
  }

  return <Outlet key={organization?.id ?? orgSlug} />;
};

export function OrgRouteWrapper() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const location = useLocation();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { isLoaded: orgLoaded, organization } = useOrganization();
  const {
    userMemberships,
    isLoaded: membershipsLoaded,
  } = useOrgMemberships();

  const [graceElapsed, setGraceElapsed] = useState(false);
  const graceTimer = useRef<ReturnType<typeof setTimeout>>();
  const revalidatedRef = useRef(false);
  const userMembershipsRef = useRef(userMemberships);
  userMembershipsRef.current = userMemberships;

  const hasMembers = (userMemberships?.data?.length ?? 0) > 0;
  // The shared memberships list is fetched once for the whole shell. A user who
  // just created or joined an org can carry a stale empty list while Clerk
  // already has the org active: trust the active org over the cached list.
  const activeOrgMatchesRoute = !!orgSlug && organization?.slug === orgSlug;
  const allLoaded = authLoaded && orgLoaded && membershipsLoaded;

  useEffect(() => {
    if (!allLoaded || hasMembers || activeOrgMatchesRoute) {
      if (graceTimer.current) {
        clearTimeout(graceTimer.current);
        graceTimer.current = undefined;
      }
      setGraceElapsed(false);
      return;
    }

    // Refetch the cached list once before giving up on it: an empty list fetched
    // before the org existed would otherwise send the user to /no-organization,
    // which sees the org with its own fresh query and sends them back here.
    if (!revalidatedRef.current) {
      revalidatedRef.current = true;
      void userMembershipsRef.current?.revalidate?.();
    }

    if (!graceElapsed && !graceTimer.current) {
      graceTimer.current = setTimeout(() => setGraceElapsed(true), ZERO_MEMBERSHIPS_GRACE_MS);
    }

    return () => {
      if (graceTimer.current) {
        clearTimeout(graceTimer.current);
        graceTimer.current = undefined;
      }
    };
  }, [allLoaded, hasMembers, activeOrgMatchesRoute, graceElapsed]);

  if (!authLoaded) return <Spinner />;
  if (!isSignedIn) {
    // Preserve deep links (e.g. billing promo codes) across sign-in.
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const signInPath = `/sign-in?return_to=${encodeURIComponent(returnTo)}`;
    return <Navigate to={signInPath} replace />;
  }
  if (!orgLoaded || !membershipsLoaded) return <Spinner />;

  if (!hasMembers && !activeOrgMatchesRoute) {
    if (!graceElapsed) return <Spinner />;
    return <Navigate to={PUBLIC_ROUTES.NO_ORGANIZATION} replace />;
  }

  if (!orgSlug) return <Navigate to="/" replace />;

  const firstSlug = userMemberships?.data?.[0]?.organization?.slug ?? undefined;

  return (
    <WrapperContent
      orgSlug={orgSlug}
      firstSlug={firstSlug}
      memberships={userMemberships?.data}
      membershipsLoaded={membershipsLoaded}
    />
  );
}
